<template>
  <fragment>
    <mc-app-header title="Detailed Analytics" />
    <mc-app-body>
      <div class="section home-page">
        <div class="columns home-page__filters">
          <div class="column">
            <b-field label="Select Concept">
              <mc-concept-select
                v-model="selectedConcept"
                @input="filterChangeHandler"
              ></mc-concept-select>
            </b-field>
          </div>
          <div class="column is-half">
            <b-field label="Select Groups/User">
              <b-field>
                <b-select
                  v-model="selectedFilterType"
                  placeholder="Type"
                  @input="filterChangeHandler"
                >
                  <option value="user">User</option>
                  <option value="groups">Group</option>
                </b-select>
                <mc-group-select
                  v-if="selectedFilterType == 'groups'"
                  v-model="selectedGroups"
                  class="home-page__filters__vue-select"
                  multiple
                  @input="filterChangeHandler"
                ></mc-group-select>
                <mc-user-select
                  v-else
                  v-model="selectedUser"
                  class="home-page__filters__vue-select"
                  @input="filterChangeHandler"
                ></mc-user-select>
              </b-field>
            </b-field>
          </div>
          <div class="column">
            <b-field label="Select date range">
              <b-datepicker
                v-model="dateRange"
                placeholder="select the date range"
                range
                @input="filterChangeHandler"
              >
              </b-datepicker>
            </b-field>
          </div>
        </div>
        <div v-if="conceptAttempts.loadingPage" class="home-page__loading">
          <b-loading active :is-full-page="false">
            <div class="loading-icon"></div>
            <span>Loading Concept Attempts</span>
          </b-loading>
        </div>
        <div
          v-else-if="hasValidInputData"
          class="tile is-ancestor home-page__results"
        >
          <b-tabs
            v-if="conceptAttempts.list.length"
            v-model="selectedTab"
            class="container"
          >
            <b-tab-item
              v-for="version in conceptVersionsFromLoadedAttempts"
              :key="`${version}`"
              :label="version"
            >
              <template slot="header">
                Version {{ version }}
                <b-icon
                  v-if="isVersionLoaded(version)"
                  size="is-small"
                  type="is-success"
                  icon="checkbox-marked-circle"
                ></b-icon>
              </template>
              <div
                v-if="versionedConcepts.loading || triggerLoad(version)"
                class="home-page__loading"
              >
                <b-loading active :is-full-page="false">
                  <div class="loading-icon"></div>
                  <span>Loading the related concept version</span>
                </b-loading>
              </div>
              <div v-else-if="versionedConcepts.loadError[version]">
                <mc-error-layout
                  :message="
                    `Couldn't load related concept: ${
                      versionedConcepts.loadError[version].message
                    }`
                  "
                >
                </mc-error-layout>
              </div>
              <DetailedAttemptAnalytics
                v-else
                :concept-attempts="getAttempts(version)"
                :versioned-concept="getConcept(version)"
              ></DetailedAttemptAnalytics>
            </b-tab-item>
          </b-tabs>
          <div v-else>
            <mc-error-layout message="There are no matching attempts!">
            </mc-error-layout>
          </div>
        </div>
        <div v-else>
          <mc-error-layout message="Please select both concept and groups.">
          </mc-error-layout>
        </div>
      </div>
    </mc-app-body>
  </fragment>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { Timestamp } from '../../firebase';
import uniq from 'lodash/uniq';
import sortBy from 'lodash/sortBy';

import DetailedAttemptAnalytics from './DetailedAttemptAnalytics';

export default {
  data() {
    return {
      selectedTab: 0,
      selectedConcept: null,
      selectedGroups: [],
      dateRange: [],
      selectedUser: null,
      selectedFilterType: 'groups'
    };
  },
  computed: {
    ...mapState(['conceptAttempts']),
    ...mapState(['versionedConcepts']),
    hasValidInputData() {
      return (
        this.selectedConcept &&
        ((this.selectedFilterType === 'groups' && this.selectedGroups.length) ||
          this.selectedUser)
      );
    },
    conceptVersionsFromLoadedAttempts() {
      if (!this.hasValidInputData || this.conceptAttempts.loading) {
        return [];
      }
      return sortBy(
        uniq(
          this.conceptAttempts.list.map(
            attempt => attempt['versionedConceptId'].split('_')[1]
          )
        )
      ).reverse();
    }
  },
  methods: {
    ...mapActions('conceptAttempts', ['setFilter']),
    ...mapActions('versionedConcepts', ['setConcept', 'loadVersion']),
    getAttempts(version) {
      return this.conceptAttempts.list.filter(
        attempt =>
          attempt['versionedConceptId'] ===
          `${this.versionedConcepts.conceptId}_${version}`
      );
    },
    isVersionLoaded(version) {
      return Boolean(this.versionedConcepts.loaded[version]);
    },
    triggerLoad(version) {
      if (this.versionedConcepts.loaded[version]) {
        return false;
      } else if (
        this.selectedTab !==
        this.conceptVersionsFromLoadedAttempts.indexOf(version)
      ) {
        return true;
      } else {
        this.loadVersion(version);
        return true;
      }
    },
    getConcept(version) {
      return this.versionedConcepts.loaded[version];
    },
    async filterChangeHandler() {
      if (this.selectedConcept) {
        this.setConcept(this.selectedConcept.id);
        this.selectedTab = 0;
      }
      if (this.hasValidInputData) {
        try {
          const filter = {
            concept: `concepts/${this.selectedConcept.id}`,
            tags:
              this.selectedFilterType === 'groups'
                ? this.selectedGroups.map(g => g.id)
                : [],
            user:
              this.selectedFilterType === 'user'
                ? `users/${this.selectedUser.id}`
                : null,
            createdAt: this.dateRange.map(d => Timestamp.fromDate(d))
          };
          this.setFilter(filter);
        } catch (error) {
          this.$buefy.toast.open({
            message: error.message,
            type: 'is-danger'
          });
        }
      }
    }
  },
  components: {
    DetailedAttemptAnalytics
  }
};
</script>

<style lang="scss" scoped>
.home-page {
  &__loading {
    min-height: 30rem;
  }
  &__filters {
    &__vue-select {
      flex-grow: 1;
    }
  }
  &__results {
    &__question-name {
      overflow: hidden;
      height: 3rem;
    }
  }
}
</style>
